<template>
  <div :class="[ns.b(), ns.is('disabled', disabled)]">
    <z-tooltip ref="popperRef" :role="role" :effect="effect" :fallback-placements="['bottom', 'top']"
      :popper-options="popperOptions" :gpu-acceleration="false" :placement="placement"
      :popper-class="[ns.e('popper'), popperClass!]" :popper-style="popperStyle" :trigger="trigger"
      :trigger-keys="triggerKeys" :trigger-target-el="contentRef" :show-arrow="showArrow"
      :show-after="trigger === 'hover' ? showTimeout : 0" :hide-after="trigger === 'hover' ? hideTimeout : 0"
      :virtual-ref="virtualRef ?? triggeringElementRef" :virtual-triggering="virtualTriggering || splitButton"
      :disabled="disabled" :transition="`${ns.namespace.value}-zoom-in-top`" :teleported="teleported"
      :append-to="appendTo" pure focus-on-target :persistent="persistent" @before-show="handleBeforeShowTooltip"
      @show="handleShowTooltip" @before-hide="handleBeforeHideTooltip">
      <template #content>
        <z-scrollbar ref="scrollbar" :wrap-style="wrapStyle" tag="div" :view-class="ns.e('list')">
          <z-roving-focus-group :loop="loop" :current-tab-id="currentTabId" orientation="horizontal"
            @current-tab-id-change="handleCurrentTabIdChange">
            <slot name="dropdown" />
          </z-roving-focus-group>
        </z-scrollbar>
      </template>
      <template v-if="!splitButton" #default>
        <z-only-child :id="triggerId" ref="triggeringElementRef" role="button" :tabindex="tabindex">
          <slot name="default" />
        </z-only-child>
      </template>
    </z-tooltip>
    <template v-if="splitButton">
      <z-button-group>
        <z-button ref="referenceElementRef" v-bind="buttonProps" :size="dropdownSize" :type="type" :disabled="disabled"
          :tabindex="tabindex" @click="handlerMainButtonClick">
          <slot name="default" />
        </z-button>
        <z-button :id="triggerId" ref="triggeringElementRef" v-bind="buttonProps" role="button" :size="dropdownSize"
          :type="type" :class="ns.e('caret-button')" :disabled="disabled" :tabindex="tabindex"
          :aria-label="t('z.dropdown.toggleDropdown')">
          <z-icon :class="ns.e('icon')"><arrow-down /></z-icon>
        </z-button>
      </z-button-group>
    </template>
  </div>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  getCurrentInstance,
  provide,
  ref,
  toRef,
  unref,
} from 'vue'
import ZButton from '@zoryn/components/button'
import ZTooltip from '@zoryn/components/tooltip'
import ZScrollbar from '@zoryn/components/scrollbar'
import ZIcon from '@zoryn/components/icon'
import ZRovingFocusGroup from '@zoryn/components/roving-focus-group'
import { ZOnlyChild } from '@zoryn/components/slot'
import { useFormSize } from '@zoryn/components/form'
import { addUnit, ensureArray } from '@zoryn/utils'
import { ArrowDown } from '@zoryn/icons'
import { useId, useLocale, useNamespace } from '@zoryn/hooks'
import { dropdownProps } from './dropdown'
import {
  DROPDOWN_INJECTION_KEY,
  DROPDOWN_INSTANCE_INJECTION_KEY,
} from './tokens'

import type { TooltipInstance } from '@zoryn/components/tooltip'
import type { CSSProperties } from 'vue'

const { ButtonGroup: ZButtonGroup } = ZButton

export default defineComponent({
  name: 'ZDropdown',
  components: {
    ZButton,
    ZButtonGroup,
    ZScrollbar,
    ZTooltip,
    ZRovingFocusGroup,
    ZOnlyChild,
    ZIcon,
    ArrowDown,
  },
  props: dropdownProps,
  emits: ['visible-change', 'click', 'command'],
  setup(props, { emit }) {
    const _instance = getCurrentInstance()
    const ns = useNamespace('dropdown')
    const { t } = useLocale()

    const triggeringElementRef = ref()
    const referenceElementRef = ref()
    const popperRef = ref<TooltipInstance>()
    const contentRef = ref<HTMLElement>()
    const scrollbar = ref(null)
    const currentTabId = ref<string | null>(null)
    const isUsingKeyboard = ref(false)

    const wrapStyle = computed<CSSProperties>(() => ({
      maxHeight: addUnit(props.maxHeight),
    }))
    const dropdownTriggerKls = computed(() => [ns.m(dropdownSize.value)])
    const trigger = computed(() => ensureArray(props.trigger))

    const defaultTriggerId = useId().value
    const triggerId = computed<string>(() => props.id || defaultTriggerId)

    function handleClick() {
      popperRef.value?.onClose(undefined, 0)
    }

    function handleClose() {
      popperRef.value?.onClose()
    }

    function handleOpen() {
      popperRef.value?.onOpen()
    }

    const dropdownSize = useFormSize()

    function commandHandler(...args: any[]) {
      emit('command', ...args)
    }

    function onItemEnter() {
      // NOOP for now
    }

    function onItemLeave() {
      const contentEl = unref(contentRef)

      trigger.value.includes('hover') &&
        contentEl?.focus({
          preventScroll: true,
        })
      currentTabId.value = null
    }

    function handleCurrentTabIdChange(id: string) {
      currentTabId.value = id
    }

    function handleBeforeShowTooltip() {
      emit('visible-change', true)
    }

    function handleShowTooltip(event?: Event) {
      isUsingKeyboard.value = event?.type === 'keydown'
      contentRef.value?.focus()
    }

    function handleBeforeHideTooltip() {
      emit('visible-change', false)
    }

    provide(DROPDOWN_INJECTION_KEY, {
      contentRef,
      role: computed(() => props.role),
      triggerId,
      isUsingKeyboard,
      onItemEnter,
      onItemLeave,
      handleClose,
    })

    provide(DROPDOWN_INSTANCE_INJECTION_KEY, {
      instance: _instance,
      dropdownSize,
      handleClick,
      commandHandler,
      trigger: toRef(props, 'trigger'),
      hideOnClick: toRef(props, 'hideOnClick'),
    })

    const handlerMainButtonClick = (event: MouseEvent) => {
      emit('click', event)
    }

    return {
      t,
      ns,
      scrollbar,
      wrapStyle,
      dropdownTriggerKls,
      dropdownSize,
      triggerId,
      currentTabId,
      handleCurrentTabIdChange,
      handlerMainButtonClick,
      handleClose,
      handleOpen,
      handleBeforeShowTooltip,
      handleShowTooltip,
      handleBeforeHideTooltip,
      popperRef,
      contentRef,
      triggeringElementRef,
      referenceElementRef,
    }
  },
})
</script>
